import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import CancelService from '../../services/CancelService';
import ErrorService from '../../services/ErrorService';
import FieldNames from '../../defaults/FieldNames.json';
import { processSubstitution } from '../../utils/CancelFeatureHelper.tsx';

interface CancelActionDialogProps {
    open: boolean;
    onClose: () => void;
    onSave?: (data: {
        reason: string;
        subReason: string;
        notes: string;
        updatedFeature: any;
    }) => void;
    onUncancel?: () => void;
    actionName?: string;
    cancelFeature?: any;
    editableFeature?: any;
}

/**
 * CancelActionDialog - A clean, simple dialog for canceling actions
 *
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onSave - Callback when save completes (receives { reason, subReason, notes, updatedFeature })
 * @param {function} onUncancel - Callback when un-cancel is clicked
 * @param {string} actionName - Optional name of the action being canceled
 * @param {object} cancelFeature - Optional existing cancel feature to populate form
 * @param {object} editableFeature - The feature being canceled (Esri graphic shape or flat row)
 */
export default function CancelActionDialog({
    open,
    onClose,
    onSave,
    onUncancel,
    cancelFeature,
    editableFeature,
    actionName = '',
}: CancelActionDialogProps) {
    const userInfo = useSelector((state: any) => state.globalContext.userInfo);
    const [fullCancelReasons, setFullCancelReasons] = useState<any[]>([]);
    const [cancelReasons, setCancelReasons] = useState<string[]>([]);
    const [cancelSubReasons, setCancelSubReasons] = useState<string[]>([]);
    const [selectedReason, setSelectedReason] = useState('');
    const [selectedSubReason, setSelectedSubReason] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Ref (not state) so toggling it doesn't itself trigger the sub-reason
    // effect below to re-run and clear the value we just hydrated.
    const isPopulatingFromExisting = useRef(false);

    const hasSubReasons = cancelSubReasons && cancelSubReasons.length > 0;

    // Fetch cancel reasons on mount
    useEffect(() => {
        if (open) {
            fetchCancelReasons();
        }
    }, [open]);

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setSelectedReason('');
            setSelectedSubReason('');
            setNotes('');
            setError(null);
            setSaving(false);
            isPopulatingFromExisting.current = false;
        }
    }, [open]);

    // Update sub-reasons when main reason changes
    useEffect(() => {
        if (!selectedReason) {
            setCancelSubReasons([]);
            if (!isPopulatingFromExisting.current) {
                setSelectedSubReason('');
            }
            return;
        }

        const subReasons = fullCancelReasons
            .filter(
                (cr) =>
                    cr[FieldNames.cancelFields.reason] === selectedReason &&
                    cr[FieldNames.cancelFields.subReason]
            )
            .map((cr) => cr[FieldNames.cancelFields.subReason]);
        const uniqueSubReasons = [...new Set(subReasons)];

        // Only clear sub-reason if not populating from existing data
        if (!isPopulatingFromExisting.current) {
            setSelectedSubReason('');
        }

        setCancelSubReasons(uniqueSubReasons as string[]);

        // Consume the populating flag after this run so future manual
        // reason changes behave normally (clear the sub-reason).
        isPopulatingFromExisting.current = false;
    }, [selectedReason, fullCancelReasons]);

    // Populate form with existing cancel feature data
    useEffect(() => {
        if (open && cancelFeature && fullCancelReasons.length > 0) {
            const existingReason = cancelFeature[FieldNames.cancelFields.cancelReason];
            const existingSubReason = cancelFeature[FieldNames.cancelFields.cancelSubReason];

            if (existingReason) {
                isPopulatingFromExisting.current = true;
                setSelectedReason(existingReason);

                // Set sub-reason if it exists
                if (existingSubReason) {
                    setSelectedSubReason(existingSubReason);
                }
            }
        }
    }, [open, cancelFeature, fullCancelReasons]);

    const fetchCancelReasons = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await CancelService.getCancelReasons();
            if (response && response.length > 0) {
                // Store full cancel reasons for sub-reason filtering
                setFullCancelReasons(response);

                // Extract unique main reasons from response
                const reasons = response
                    .map((item: any) => item[FieldNames.cancelFields.reason])
                    .filter((reason: any) => reason); // Filter out null/undefined
                const uniqueReasons = [...new Set(reasons)];
                setCancelReasons(uniqueReasons as string[]);
            } else {
                setFullCancelReasons([]);
                setCancelReasons([]);
            }
        } catch (err) {
            setError('Failed to load cancel reasons');
            ErrorService.addErrorLogEntry(err as any);
            setFullCancelReasons([]);
            setCancelReasons([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedReason) {
            setError('Please select a cancel reason');
            return;
        }

        if (hasSubReasons && !selectedSubReason) {
            setError('Please select a cancel sub-reason');
            return;
        }

        setError(null);
        setSaving(true);
        try {
            const featureAttr = editableFeature?.attributes ?? editableFeature;
            const updatedFeature = await processSubstitution({
                featureAttr,
                editableFeature,
                cancelReasonSelection: selectedReason,
                cancelSubReasonSelection: selectedSubReason,
                cancelHasSubstitute: false,
                cancelSubstituteSelection: undefined,
                cancelReasoning: notes.trim(),
                userInfo,
                canceled: true,
            });

            if (onSave) {
                onSave({
                    reason: selectedReason,
                    subReason: selectedSubReason,
                    notes: notes.trim(),
                    updatedFeature,
                });
            }

            handleClose();
        } catch (err) {
            setError('Failed to save cancellation');
            ErrorService.addErrorLogEntry(err as any);
        } finally {
            setSaving(false);
        }
    };

    const handleUncancel = () => {
        if (onUncancel) {
            onUncancel();
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };
    // not all cancel reasons have sub-reasons, so we only require sub-reason if the selected reason has them
    const isSaveDisabled =
        !selectedReason || (hasSubReasons && !selectedSubReason) || loading || saving;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>Cancel Action</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    {actionName && (
                        <Typography variant='body1' sx={{ mb: 2, fontWeight: 500 }}>
                            Action: {actionName}
                        </Typography>
                    )}

                    {/* Cancel Reason Dropdown */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <Typography variant='body2' sx={{ mb: 1 }}>
                            Cancel Reason <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <Select
                            value={selectedReason}
                            onChange={(e) => setSelectedReason(e.target.value)}
                            disabled={loading}
                            displayEmpty
                            error={!!error && !selectedReason}
                        >
                            <MenuItem value='' disabled>
                                <em>Select a reason</em>
                            </MenuItem>
                            {loading ? (
                                <MenuItem disabled>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    Loading reasons...
                                </MenuItem>
                            ) : (
                                cancelReasons.map((reason) => (
                                    <MenuItem key={reason} value={reason}>
                                        {reason}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>

                    {/* Cancel Sub-Reason Dropdown (conditional) */}
                    {hasSubReasons && (
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <Typography variant='body2' sx={{ mb: 1 }}>
                                Cancel Sub-Reason <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <Select
                                value={selectedSubReason}
                                onChange={(e) => setSelectedSubReason(e.target.value)}
                                disabled={loading}
                                displayEmpty
                                error={!!error && hasSubReasons && !selectedSubReason}
                            >
                                <MenuItem value='' disabled>
                                    <em>Select a sub-reason</em>
                                </MenuItem>
                                {cancelSubReasons.map((subReason) => (
                                    <MenuItem key={subReason} value={subReason}>
                                        {subReason}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Notes Text Field */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <Typography variant='body2' sx={{ mb: 1 }}>
                            Notes
                        </Typography>
                        <TextField
                            multiline
                            rows={4}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder='Enter notes for cancellation...'
                            variant='outlined'
                            fullWidth
                        />
                    </FormControl>

                    {/* Error Message */}
                    {error && (
                        <Typography variant='body2' color='error' sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={handleClose} disabled={saving}>
                    Close
                </Button>
                {onUncancel && (
                    <Button
                        variant='outlined'
                        color='warning'
                        onClick={handleUncancel}
                        disabled={saving}
                    >
                        Un-cancel
                    </Button>
                )}
                <Button variant='contained' onClick={handleSave} disabled={isSaveDisabled}>
                    {saving ? <CircularProgress size={20} color='inherit' /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
